import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  getAccountabilitySeries,
  getAnomalyDetection,
  getFlowHealth,
  getMeterLocationList,
  getSchedulerTimeline,
  getSystemBattery,
  getSystemUsage,
  getWaterLeak,
} from "../../services";
import { useDistrict } from "../../utils/DistrictContext";
import { ALERT_REFRESH_MINUTES, SCHEDULER_CHECK_MINUTES } from "./alertCounts";

// ---------------------------------------------------------------------------
// The district data behind the New Dashboard.
//
// Every feed is a live API call - the same endpoints the rest of the app
// already uses - reshaped here into the payloads the MizuWatch 3.3 design was
// built on, so the cards, the map, the answer board and the original build in
// LegacyFrame (sent them as nd-feeds) read them unchanged. Swapping a source is
// a change to this file alone.
//
//   meters          apis/meter_location_list     (Digital Twin map view)
//   flowHealth      apis/flow_health             (Flow Health page) for
//                   backflow and no recent flow, with the leaks from
//                   apis/get_water_leak          (Leak Flow page)
//   anomaly         apis/anoamaly_detection      (Analytics -> Anomaly)
//   accountability  apis/accountability          (Analytics -> Accountability)
//   systemHealth    apis/system_battery          (System Health page)
//   usage           apis/system_usage            (System Water Usage widget)
//
// Still bundled with the design, because nothing serves them yet: the pipeline
// and GIS asset geometry, and the files of the tabs not rebuilt yet (Efficiency's
// water_usage.json and total_consumption.json, Hydraulics' network model and
// scenarios). The original build's static copies of these feeds are gone - in
// LegacyFrame these are its only source.
// ---------------------------------------------------------------------------

export const FEED_BASE = `${import.meta.env.BASE_URL}mizuwatch-2.0/`;

// ── Live: meters ────────────────────────────────────────────────────────────
// Every active meter with its location and health, from the same API the
// Digital Twin map view uses. The design's meter_location_only_active export
// is this API's output, so the map, the address lookup and the original
// build read it unchanged. Duplicates (one meter listed twice at a location)
// are dropped the way the Digital Twin map drops them.
async function fetchMeters() {
  const body = await getMeterLocationList("active");
  const rows = Array.isArray(body?.data?.meters) ? body.data.meters : [];
  const seen = new Set();
  return rows
    .filter((meter) => {
      if (!meter || meter.meter_id == null) return false;
      const key = `${meter.location_id}-${meter.meter_id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    // The API joins the two address lines with a space, so a missing second
    // line leaves one at the end.
    .map((meter) => ({ ...meter, location_address: String(meter.location_address || "").trim() }));
}

// ── Live: anomaly ───────────────────────────────────────────────────────────
// The same endpoint as the Analytics → Anomaly Detection page, every category,
// over the PAST DAY - the dashboard shows what is happening now, not the
// month (the page's "D" chip). It answers with one flat meter list;
// regrouped here into the design's `category_breakdown` shape so the card, the
// map and the answer board read it unchanged.
const ANOMALY_TIME_RANGE = "past_day";

async function fetchAnomaly() {
  const body = await getAnomalyDetection("ALL", ANOMALY_TIME_RANGE);
  const rows = Array.isArray(body?.data) ? body.data : [];
  const byCategory = new Map();
  rows.forEach((row) => {
    if (!row || row.meter_id == null) return;
    const name = row.category || "Anomaly";
    if (!byCategory.has(name)) byCategory.set(name, []);
    // Gallons, as the Analytics page shows - highest_flow alone may be in the
    // category's own unit.
    const flow = Number(row.highest_flow_gallons ?? row.highest_flow) || 0;
    byCategory.get(name).push({
      meter_id: String(row.meter_id),
      // A tiny negative peak would otherwise round to "-0 GAL".
      highest_flow: Math.round(flow) === 0 ? 0 : flow,
      highest_flow_time: row.highest_flow_time
        ? dayjs(row.highest_flow_time).format("MMM D, YYYY")
        : "",
    });
  });
  const category_breakdown = [...byCategory].map(([category_name, data]) => ({
    category_name,
    anomaly_meter_count: data.length,
    anomaly_data: { data },
  }));
  return {
    category_breakdown,
    start_date: body?.start_date,
    end_date: body?.end_date,
    total_anomaly_meters: new Set(rows.map((row) => String(row?.meter_id))).size,
    total_categories: category_breakdown.length,
  };
}

// ── Live: leaks ─────────────────────────────────────────────────────────────
// The same call as the Leak Flow page (every category, meter-level rows), over
// the PAST DAY - the page's "D" chip - so the dashboard shows current leaks.
// Each row is renamed onto the flow-health leak fields the card, map and
// answer board already read.
const LEAK_INTERVAL = "Past Day";

// "Leak on High Flow Detected: 1,120 gal/hr beginning ..." -> 1120
function leakGallonsPerHour(row) {
  const match = String(row.leak_message || "").match(/([\d,]+(?:\.\d+)?)\s*gal\/hr/i);
  return match ? Number(match[1].replace(/,/g, "")) : Number(row.leak_rate) || 0;
}

async function fetchLeaks() {
  const body = await getWaterLeak("ALL", LEAK_INTERVAL, "details");
  const rows = Array.isArray(body?.meters) ? body.meters : [];
  const meters = rows
    .filter((row) => row && row.meter_id != null)
    .map((row) => {
      const gallons = Number(row.water_loss_gallons ?? row.water_loss) || 0;
      const price = parseFloat(String(row.price ?? "").replace(/[$,\s]/g, "")) || 0;
      return {
        meter_id: String(row.meter_id),
        account_name: row.account_name,
        service_address: row.service_address,
        // Where the leak is when its meter is not in the meter list - the leak
        // clusters look its location up there (mizuAi.js locateLeaks).
        location_id: row.location_id,
        category: row.category,
        register_number: row.register_number,
        leak_message: row.leak_message || "Leak detected",
        leak_rate: leakGallonsPerHour(row),
        leak_start_date: row.leak_start_date || row.week_range?.split(" - ")?.[0] || "",
        total_water_loss: `${Math.round(gallons)} Gallons`,
        total_price_for_loss: `$${Math.floor(price).toLocaleString()}`,
      };
    });
  return { meters, leak_count: body?.leak_count ?? meters.length };
}

// ── Live: flow health (backflow, no recent flow) ────────────────────────────
// apis/flow_health - the Flow Health page's call, every category. It answers
// with a list per issue (flow_data.backflow / .no_recent_flow, one row per
// meter carrying its message); turned here into the design's flow-health
// shape - id lists plus one flow_health_data row per meter, its messages
// merged - which every card, the map and the original build read. Its leak
// list is not used: leaks come from get_water_leak (withLiveLeaks below).
async function fetchFlowHealth() {
  const body = await getFlowHealth();
  const flow = body?.data?.flow_data || {};
  const rows = new Map();
  const ids = { backflow: new Set(), no_recent_flow: new Set() };
  // Being in the list is what flags a meter - the Flow Health page lists a
  // row and the donut counts it even when its message came back empty (the
  // page shows "-"). So every row counts; one without a message gets a plain
  // one, since the cards and the map only treat a row as flagged when it has
  // a message.
  [
    ["backflow", "backflow_message", "Backflow detected"],
    ["no_recent_flow", "no_recent_message", "No recent flow"],
  ].forEach(([type, messageKey, fallback]) => {
    (Array.isArray(flow[type]) ? flow[type] : []).forEach((meter) => {
      if (!meter || meter.meter_id == null) return;
      const id = String(meter.meter_id);
      ids[type].add(id);
      const raw = meter[messageKey];
      const message = raw && raw !== "-" ? raw : meter.message || fallback;
      const row = rows.get(id) || { meter_id: id, leak_message: null };
      rows.set(id, { ...meter, ...row, meter_id: id, [messageKey]: message });
    });
  });
  if (!ids.backflow.size && !ids.no_recent_flow.size) {
    // Says which it is: no rows at all, or rows the lists did not carry.
    console.warn("Flow health: no backflow / no-recent-flow rows", {
      counts: body?.data?.flow_data_per,
      listKeys: Object.keys(flow),
    });
  }
  return {
    leak_meter: [],
    backflow_meter: [...ids.backflow],
    no_recent_flow_meter: [...ids.no_recent_flow],
    all_meter: [...new Set([...ids.backflow, ...ids.no_recent_flow])],
    flow_health_data: [...rows.values()],
  };
}

// The flow-health payload with its leak list and leak rows swapped for the
// Leak Flow page's - backflow and no-recent-flow rows are kept as they are.
function withLiveLeaks(flowHealth, leak) {
  const rows = Array.isArray(flowHealth.flow_health_data) ? flowHealth.flow_health_data : [];
  const leakIds = [...new Set(leak.meters.map((row) => row.meter_id))];
  return {
    ...flowHealth,
    leak_meter: leakIds,
    all_meter: [...new Set([...(flowHealth.all_meter || []), ...leakIds])],
    flow_health_data: [
      ...rows.map((row) => (row.leak_message ? { ...row, leak_message: null } : row)),
      ...leak.meters,
    ],
  };
}

// ── Live: system health ─────────────────────────────────────────────────────
// apis/system_battery - the old dashboard's System Health widget. One call
// answers both of the design's system-health exports, rebuilt here in their
// shapes: `systemHealth` (the overall % and each layer's % and counts) and
// `systemHealthMeters` (the meter ids behind each layer). The percentages are
// the API's own; the counts come from its per-layer detail lists.
const idsOf = (rows) =>
  [...new Set((Array.isArray(rows) ? rows : []).map((row) => row?.meter_id).filter((id) => id != null).map(String))];

async function fetchSystemHealth() {
  const body = await getSystemBattery();
  const live = body?.data || {};
  const inner = live.data?.system_health_data || live.data || {};
  const communicationMeters = idsOf(inner.communication_data);
  const dataMeters = idsOf(inner.outlier_data);
  const deviceMeters = idsOf(inner.device_data);
  const allIssue = [...new Set([...communicationMeters, ...dataMeters, ...deviceMeters])];
  const total = Number(inner.all_meter_count) || 0;
  const count = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
  // A missing percentage stays null rather than becoming NaN% on screen.
  const pct = (value) => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));
  const commCount = count(inner.communication_data_count, communicationMeters.length);
  const deviceCount = count(inner.device_data_count, deviceMeters.length);
  const outlierCount = count(inner.outlier_count, dataMeters.length);
  const dataPct = pct(live.data_status);
  return {
    systemHealth: {
      data: {
        system_health: pct(live.system_health),
        device_comm_status: {
          all_meter_count: total,
          comm_status: pct(live.comm_status),
          communication_status_count: Math.max(total - commCount, 0),
          device_status: pct(live.device_status),
          device_status_count: Math.max(total - deviceCount, 0),
        },
        data_status: {
          all_meter_count: total,
          good_percentage: dataPct,
          bad_percentage: dataPct == null ? null : 100 - dataPct,
          good_meter_count: Math.max(total - outlierCount, 0),
          outliers_include_count: outlierCount,
        },
        system_health_data: inner,
      },
    },
    systemHealthMeters: {
      communication_meter: communicationMeters,
      data_meter: dataMeters,
      device_meter: deviceMeters,
      error_comnine: allIssue,
      all_meter: allIssue,
    },
  };
}

// ── Live: accountability ────────────────────────────────────────────────────
// The past week, one value per day, for each of the card's three tabs - the
// same call as Analytics -> Flow Analytics -> Accountability. The daily figure
// is the one that page charts as Accountability: including maintenance and
// temporary meters, falling back to the plain figure when that is absent.
const ACCOUNTABILITY_SYSTEMS = ["total", "potable", "non_potable"];

// One system's days over `range` ({ start, end }, "YYYY-MM-DD", end inclusive)
// - the past week when none.
async function fetchAccountabilitySystem(system, range = null) {
  const body = await getAccountabilitySeries(system, range ? "Custom" : "Past Week", "Days", 1, range);
  const data = body?.data || {};
  const base = data.accountability_data?.accountability || [];
  const combined =
    data.accountability_with_maintainance_and_temp_meter_data
      ?.accountability_with_maintainance_and_temp_meter || [];
  const days = base.flatMap((item, index) => {
    // The series has a row for every day of the range: one with no supply
    // recorded is a day with no readings yet (a range reaching past the
    // latest data), not a day at 0%.
    if (item.supply_flow != null && Number(item.supply_flow) === 0) return [];
    const raw = combined[index]?.accountability ?? item.accountability;
    // "Oct 05, 2026" -> { date: "Oct 5", day: "Mon" }
    const when = dayjs(item.option);
    return [{
      date: when.isValid() ? when.format("MMM D") : String(item.option ?? ""),
      day: when.isValid() ? when.format("ddd") : "",
      iso: when.isValid() ? when.format("YYYY-MM-DD") : null,
      value: Math.round(Number(raw) || 0),
    }];
  });
  return { days };
}

// Each system's days over `range`, and how many systems answered.
async function loadAccountability(range = null) {
  // One system at a time, as the Analytics page asks for them: three of these
  // heavy requests at once left one (Potable) failing and the tab empty.
  // Each system gets one retry, and a failure only empties its own tab.
  const result = {};
  let answered = 0;
  for (const system of ACCOUNTABILITY_SYSTEMS) {
    let days = null;
    for (let attempt = 1; attempt <= 2 && days == null; attempt += 1) {
      try {
        ({ days } = await fetchAccountabilitySystem(system, range));
      } catch (error) {
        console.warn(`Accountability (${system}) attempt ${attempt} failed`, error?.response?.status, error);
      }
    }
    if (days != null) answered += 1;
    result[system] = { days: days || [] };
  }
  return { result, answered };
}

async function fetchAccountability() {
  const { result } = await loadAccountability();
  ACCOUNTABILITY_SYSTEMS.forEach((system) => {
    if (!result[system].days.length) console.warn(`Accountability (${system}): no daily rows for the past week`);
  });
  return result;
}

/**
 * The seven days to `date` ("YYYY-MM-DD", itself included), each system's -
 * the week the Digital Twin's Historical view shows for a past date, in the
 * accountability feed's shape. Rejects when no system answered.
 */
export async function fetchAccountabilityWeek(date) {
  const range = { start: dayjs(date).subtract(6, "day").format("YYYY-MM-DD"), end: date };
  const { result, answered } = await loadAccountability(range);
  if (!answered) throw new Error(`Accountability for ${range.start} to ${range.end} could not be loaded`);
  return result;
}

// ── Live: usage ─────────────────────────────────────────────────────────────
// The System Water Usage widget's call, for the past week: the district's
// usage over the last seven days, which the outlook carries forward.
async function fetchUsage() {
  const body = await getSystemUsage("Past Week");
  const usage = body?.data?.systemusage || {};
  const gallons = [usage.flow_gallons, usage.flow]
    .map((value) => (value == null || value === "" ? NaN : Number(value)))
    .find((value) => Number.isFinite(value));
  return { week_gallons: gallons ?? null, range: usage.this_range || "" };
}

// Each live feed, and what stands in for it when its request fails - so the
// card says there is nothing rather than loading forever.
const LIVE_FEEDS = {
  meters: { load: fetchMeters, empty: [] },
  anomaly: { load: fetchAnomaly, empty: { category_breakdown: [] } },
  leak: { load: fetchLeaks, empty: { meters: [], leak_count: 0 } },
  // Split into feeds.systemHealth and feeds.systemHealthMeters below.
  systemHealthLive: {
    load: fetchSystemHealth,
    empty: { systemHealth: { data: {} }, systemHealthMeters: {} },
  },
  flowHealth: {
    load: fetchFlowHealth,
    empty: { leak_meter: [], backflow_meter: [], no_recent_flow_meter: [], all_meter: [], flow_health_data: [] },
  },
  accountability: {
    load: fetchAccountability,
    empty: { total: { days: [] }, potable: { days: [] }, non_potable: { days: [] } },
  },
  usage: { load: fetchUsage, empty: { week_gallons: null, range: "" } },
};

// ── Keeping them current ────────────────────────────────────────────────────
// The feeds are fetched when the dashboard opens and when the district
// changes, and after that:
//
//   every ALERT_REFRESH_MINUTES    the alert feeds - leaks, flow health,
//                                  anomalies, system health: the live counts
//   every SCHEDULER_CHECK_MINUTES  apis/SchedulerTimeline (the Data Sources
//                                  widget's call). When the backend has
//                                  finished loading new data since the last
//                                  check (Smart Meters, Leak Logs), every feed
//   on request                     every feed - refresh(), the dashboard's
//                                  refresh buttons
//
// Nothing is fetched while the browser tab is hidden; whatever fell due runs
// when it shows again. The backend bounds how fresh a refresh can be: its
// scheduler loads meter and leak data about once a day (the Data Sources
// widget calls a load "current" within 24 hours), and apis/flow_health and
// apis/system_battery answer from its Redis cache - per district, kept up to
// 24 hours or until apis/clear_cache - so a new flow-health scan shows once
// that cache is cleared. Leaks and anomalies are read live each time.
//
// A refresh that fails keeps the data on screen (its feed is marked stale,
// with the error); only a first load that fails leaves a feed empty.
// (The intervals live with the alert counts' definitions, which describe them.)
const ALERT_FEEDS = ["leak", "flowHealth", "anomaly", "systemHealthLive"];
// The scheduler tasks (as apis/SchedulerTimeline names them) the feeds read.
export const SCHEDULER_TASKS = ["Smart Meters", "Leak Logs"];

/**
 * Every feed, each arriving independently: a panel renders as soon as the
 * feed it needs lands rather than waiting on the slowest. `errors` names any
 * feed whose latest fetch failed, so a panel can say so instead of spinning
 * forever; `fetchedAt` when each last answered successfully - the time the
 * data on screen was fetched. `refresh()` fetches everything now, `refreshing`
 * is true while any feed is on its way, and `backend` holds the scheduler's
 * latest loads ({ tasks: [{ name, endTime }], checkedAt }).
 */
export default function useDashboardFeeds() {
  const [loaded, setLoaded] = useState({});
  const [errors, setErrors] = useState({});
  const [fetchedAt, setFetchedAt] = useState({});
  const [inFlight, setInFlight] = useState(0);
  const [backend, setBackend] = useState(null);
  const { selectedDistrict } = useDistrict() || {};

  // A district change starts over: answers for the district before are
  // dropped (generation), and so are its feeds' "already has data" marks.
  const generation = useRef(0);
  const busy = useRef(new Set());
  const hasData = useRef(new Set());
  const lastAlertRefresh = useRef(0);
  const lastSchedulerCheck = useRef(0);
  // Each scheduler task's latest finished load, as last seen.
  const lastLoads = useRef(null);

  // Fetches these feeds - any already on its way is left to finish.
  const fetchFeeds = useCallback((keys) => {
    const gen = generation.current;
    const todo = keys.filter((key) => !busy.current.has(key));
    todo.forEach((key) => busy.current.add(key));
    setInFlight(busy.current.size);
    return Promise.all(
      todo.map((key) => {
        const { load, empty } = LIVE_FEEDS[key];
        // One retry after a short pause: these heavy calls go out together on
        // load, and a single failed request otherwise blanks a whole panel
        // (Meter Estate 0, Backflow 0) until the next reload.
        return load()
          .catch((error) => {
            console.warn(`${key} feed failed, retrying once`, error?.response?.status, error);
            return new Promise((resolve) => setTimeout(resolve, 1500)).then(load);
          })
          .then((payload) => {
            if (gen !== generation.current) return;
            hasData.current.add(key);
            setLoaded((current) => ({ ...current, [key]: payload }));
            setFetchedAt((current) => ({ ...current, [key]: Date.now() }));
            setErrors((current) => {
              if (!(key in current)) return current;
              const next = { ...current };
              delete next[key];
              return next;
            });
          })
          .catch((error) => {
            if (gen !== generation.current) return;
            console.error(`Error fetching ${key} feed`, error);
            // What is on screen stays; only a feed that never loaded stands
            // in empty.
            if (!hasData.current.has(key)) setLoaded((current) => ({ ...current, [key]: empty }));
            setErrors((current) => ({ ...current, [key]: error?.message || `${key}: request failed` }));
          })
          .finally(() => {
            if (gen !== generation.current) return;
            busy.current.delete(key);
            setInFlight(busy.current.size);
          });
      }),
    );
  }, []);

  const refreshAll = useCallback(() => {
    lastAlertRefresh.current = Date.now();
    return fetchFeeds(Object.keys(LIVE_FEEDS));
  }, [fetchFeeds]);

  // The scheduler's latest finished loads; every feed again when one is new.
  const checkScheduler = useCallback(async () => {
    const gen = generation.current;
    lastSchedulerCheck.current = Date.now();
    try {
      const body = await getSchedulerTimeline();
      if (gen !== generation.current) return;
      const rows = body?.success && Array.isArray(body.data) ? body.data : [];
      // A load under way has no end time yet: its last finished one stands.
      const seen = { ...(lastLoads.current || {}) };
      rows.forEach((row) => {
        const end = Date.parse(row?.end_time);
        if (SCHEDULER_TASKS.includes(row?.task_name) && Number.isFinite(end) && !(seen[row.task_name] >= end)) {
          seen[row.task_name] = end;
        }
      });
      const newer = lastLoads.current != null && SCHEDULER_TASKS.some((task) => seen[task] > (lastLoads.current[task] || 0));
      lastLoads.current = seen;
      setBackend({
        tasks: SCHEDULER_TASKS.filter((task) => seen[task]).map((task) => ({ name: task, endTime: seen[task] })),
        checkedAt: Date.now(),
      });
      if (newer) refreshAll();
    } catch (error) {
      console.warn("Scheduler timeline check failed", error?.response?.status, error);
    }
  }, [refreshAll]);

  // The feeds follow the selected district (sent as the District header).
  useEffect(() => {
    generation.current += 1;
    busy.current = new Set();
    hasData.current = new Set();
    lastLoads.current = null;
    setLoaded({});
    setErrors({});
    setFetchedAt({});
    setInFlight(0);
    setBackend(null);
    refreshAll();
    checkScheduler();
    // Leaving (or changing district): answers still on their way are dropped.
    return () => {
      generation.current += 1;
    };
  }, [selectedDistrict, refreshAll, checkScheduler]);

  // On their timers, while the tab shows; on its return, whatever is due.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastAlertRefresh.current >= ALERT_REFRESH_MINUTES * 60000) {
        lastAlertRefresh.current = now;
        fetchFeeds(ALERT_FEEDS);
      }
      if (now - lastSchedulerCheck.current >= SCHEDULER_CHECK_MINUTES * 60000) checkScheduler();
    };
    const timer = window.setInterval(tick, 60000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [fetchFeeds, checkScheduler]);

  // Asked for: every feed now, and the backend's loads again.
  const refresh = useCallback(() => {
    checkScheduler();
    return refreshAll();
  }, [checkScheduler, refreshAll]);

  // flowHealth waits for the live leaks too, so it never shows half-built.
  // The one system-health call is split into the two feeds the panels read.
  const feeds = useMemo(() => {
    const { flowHealth, leak, systemHealthLive, ...rest } = loaded;
    return {
      ...rest,
      leak,
      flowHealth: flowHealth && leak ? withLiveLeaks(flowHealth, leak) : undefined,
      systemHealth: systemHealthLive?.systemHealth,
      systemHealthMeters: systemHealthLive?.systemHealthMeters,
    };
  }, [loaded]);

  return { feeds, errors, fetchedAt, refreshing: inFlight > 0, refresh, backend };
}
